import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BusinessGroupProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Business } from "../../assets/SideBarIcons/business.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import { GetBusinessGroupByIdResponse } from "subModule/src/services/businessgroup/interface";
import Loader from "layout/Loader";
import BusinessGroupTabs from "./BusinessGroupTabs";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}
export default function BusinessGroupsModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedBusinessGroupPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phBusinessGroupAr") : t("phBusinessGroupEn");
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "businessGroup",
    moduleHref: "/business-groups",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    BusinessGroupProfileService.getBusinessGroupByID(+(params as any)?.id)
      .then((res: GetBusinessGroupByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      BusinessGroupProfileService.updateBusinessGroup(+params?.id, { ...data })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("businessGroup") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      BusinessGroupProfileService.createBusinessGroup(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("businessGroup") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate("/business-groups");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate("/business-groups")}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("businessesGroup") : t("addBusinessGroups")}
        icon={<Business height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_BUSINESSGROUP_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      <div className={`${params?.id ? "flex" : ""}`}>
        {params?.id ? (
          <BusinessGroupTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={params?.id ? t("businessesGroup") : t("addBusinessGroups")}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-baseline mb-2"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedBusinessGroupPlaceholder(
                      primaryLanguage,
                    )}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedBusinessGroupPlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
